import { useId } from "react";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import type { SurveyOption } from "./types";

interface ChoiceQuestionProps {
  question: string;
  options: SurveyOption[];
  value: string;
  onChange: (value: string) => void;
  /** Id of the region this answer reveals, when it gates follow-up questions. */
  controls?: string;
  error?: string;
}

/**
 * One single-answer question.
 *
 * The question text is a plain `<span id>` wired up with `aria-labelledby` rather than a
 * `<label htmlFor>`: Radix renders each option as a `<button>`, and a label pointing at a
 * button is spec-legal but not honoured by NVDA or JAWS. Same reasoning as `ui/field.tsx`.
 */
export function ChoiceQuestion({
  question,
  options,
  value,
  onChange,
  controls,
  error,
}: ChoiceQuestionProps) {
  const id = useId();
  const errorId = `${id}-error`;

  return (
    <fieldset className="flex flex-col gap-2">
      <legend id={id} className="text-sm font-medium">
        {question}
      </legend>
      <RadioGroup
        value={value}
        onValueChange={onChange}
        aria-labelledby={id}
        aria-controls={controls}
        aria-describedby={error ? errorId : undefined}
        aria-invalid={error ? true : undefined}
        className="gap-2"
      >
        {options.map((option) => {
          const optionId = `${id}-${option.value}`;
          return (
            <div key={option.value} className="flex items-center gap-2">
              <RadioGroupItem
                value={option.value}
                id={optionId}
                aria-labelledby={`${optionId}-label`}
              />
              {/* Both wirings, on purpose. `htmlFor` gives the hit target — a <button> is a
                  labelable element, so clicking the text activates it — while
                  `aria-labelledby` supplies the name, because NVDA and JAWS do not reliably
                  take a button's name from a label. See ui/field.tsx. */}
              <label
                htmlFor={optionId}
                id={`${optionId}-label`}
                className="text-sm cursor-pointer"
              >
                {option.label}
              </label>
            </div>
          );
        })}
      </RadioGroup>
      {error && (
        <p id={errorId} role="alert" className="text-sm text-destructive">
          {error}
        </p>
      )}
    </fieldset>
  );
}
